import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useClient } from '../../ClientContext';
import { askAgent } from '../../reporting/askAgent';
// Formatting and labels only. runQuery and runComparison are gone from this
// page: the figures come from the warehouse now, through askAgent, and
// computing them a second time in the browser is exactly how a chat answer and
// a dashboard tile end up disagreeing.
import {
  fmt, num, shortDate, METRIC_LABELS, LOWER_IS_BETTER,
} from '../../reporting/metrics';
import { token } from '../../tokens';
import Icon from '../../components/Icon';
import './reporting.css';

/**
 * AI Insights.
 *
 * The page used to answer a question with a paragraph and a folded JSON dump.
 * Two problems with that. The figures the paragraph was written from were on
 * screen only as prose, so nothing could be scanned or checked. And askAgent
 * already fetches the previous period — the narrator is given both sides — but
 * the interface never showed it, so "up 14%" had to be taken on trust.
 *
 * So an answer is now a card: the figures, then their shape (a chart when the
 * question is about time, a ranking when it is about which), then the
 * sentence, then a plain-English record of how it was calculated. The JSON is
 * gone; a query spec written out as a sentence is readable by the person who
 * actually needs to check it, which is the account manager, not the engineer.
 *
 * The composer takes the Campaign Build treatment, because they are the same
 * gesture and should not look like two different products.
 */

/* Dimensions that produce a ranking rather than a series. */
/**
 * The label for a metric or dimension, the client's own winning.
 *
 * METRIC_LABELS is a constant in metrics.js, written for the generated data.
 * It says "Cost per purchase" where this client's registry says "Cost per
 * conversion" - and a client who renames CPA to "Cost per qualified lead"
 * would never see it, which defeats the point of a per-client registry.
 *
 * The server sends a label with every column. It is preferred here, per
 * answer rather than globally, because two clients can label the same metric
 * differently and a module-level map would show whichever was fetched last.
 * The constant remains the fallback, for pinned answers from before this
 * existed.
 */
function labelOf(labels, key) {
  return (labels && labels[key]) || METRIC_LABELS[key] || DIM_LABELS[key] || key;
}

const RANKING_DIMS = new Set(['campaign', 'adset', 'ad', 'placement', 'strategy', 'format', 'objective']);

const DIM_LABELS = {
  date: 'Date', campaign: 'Campaign', adset: 'Ad set', ad: 'Ad',
  placement: 'Placement', strategy: 'Strategy', format: 'Format', objective: 'Objective',
};

const RANGE_OPTIONS = [
  { value: null, label: 'Let the question decide' },
  { value: 7,   label: 'Last 7 days' },
  { value: 14,  label: 'Last 14 days' },
  { value: 30,  label: 'Last 30 days' },
  { value: 90,  label: 'Last 90 days' },
];

const QUICK = [
  { colour: 'var(--pine-light)', text: 'How many leads did I generate in the last 7 days?' },
  { colour: 'var(--brass)',      text: 'Which campaign has the lowest cost per lead?' },
  { colour: 'var(--info)',       text: 'How has spend trended over the last month?' },
  { colour: 'var(--bad)',        text: 'Has my cost per lead got worse, and what caused it?' },
];

const TOPICS = [
  { title: 'What changed',     blurb: 'Movement against the previous period, and what drove it',
    q: 'What has changed most against the previous period?' },
  { title: 'Cost efficiency',  blurb: 'Cost per lead, CPM and conversion rate together',
    q: 'How is my cost per lead trending, and is it spend or conversion rate driving it?' },
  { title: 'Best and worst',   blurb: 'Rank campaigns on the measure that matters',
    q: 'Rank my campaigns by cost per lead, best to worst.' },
  { title: 'Where spend went', blurb: 'Distribution across campaigns and placements',
    q: 'Where did my spend go by campaign over the last 30 days?' },
];

/* Pins are per client and bounded — a report shortlist, not a log. */
const MAX_PINS = 12;
const pinKey = (scope) => `bidsense.insights.pins.${scope}`;

function loadPins(scope) {
  try {
    const raw = localStorage.getItem(pinKey(scope));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_PINS) : [];
  } catch {
    return [];
  }
}

function savePins(scope, pins) {
  try {
    localStorage.setItem(pinKey(scope), JSON.stringify(pins.slice(0, MAX_PINS)));
  } catch {
    /* storage full or blocked — pins are a convenience, not a record */
  }
}

const rangeText = (r) => `${shortDate(r.start)} – ${shortDate(r.end)}`;

/* ─────────────────────────────────────────────────────────────────────────
   Figure strip
   ───────────────────────────────────────────────────────────────────────── */

function Figures({ metrics, totals, previous, range, limited, labels }) {
  const keys = (metrics || []).filter((k) => totals && totals[k] != null).slice(0, 4);
  if (!keys.length) return null;

  return (
    <>
      <div className="ins-figs">
        {keys.map((k) => {
          const now = totals[k];
          const was = previous ? previous[k] : null;
          const change = was ? ((now - was) / was) * 100 : null;
          const flat = change != null && Math.abs(change) < 0.5;
          const better = change == null || flat
            ? null
            : (LOWER_IS_BETTER.has(k) ? change < 0 : change > 0);

          return (
            <div className="ins-fig" key={k}>
              <div className="ins-fig-k">{labelOf(labels, k)}</div>
              <div className="ins-fig-v">{fmt(k, now)}</div>
              {change != null && Number.isFinite(change) && (
                <div className={`ins-fig-d ${flat ? 'flat' : better ? 'good' : 'bad'}`}>
                  {!flat && <Icon name={change > 0 ? 'up' : 'down'} size={11} strokeWidth={3} />}
                  {flat ? 'Level' : `${Math.abs(change).toFixed(1)}%`}
                  <span className="note">vs previous period</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {range && (
        <div className="ins-figs-cap">
          {limited
            ? `Totals across everything the question matched — including rows outside the ${limited} shown below`
            : 'Totals across everything the question matched'} · {rangeText(range)}
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Shape: a chart for time, a ranking for everything else
   ───────────────────────────────────────────────────────────────────────── */

function TimeChart({ rows, previousRows, metric, days, labels }) {
  const colours = {
    line: token('--chart-1', '#2D6B50'),
    ghost: token('--chart-ghost', '#B9BFB9'),
    grid: token('--chart-grid', '#E5E7EB'),
    axis: token('--chart-axis', '#6B7280'),
  };

  // Zipped by position: the two windows are the same length by construction
  // and the dates deliberately differ.
  const series = rows.map((row, i) => ({ ...row, prev: previousRows?.[i]?.[metric] ?? null }));
  const hasGhost = series.some((s) => s.prev != null);

  const axisFormat = (v) => {
    if (metric === 'spend' || metric === 'revenue') return v >= 1000 ? `£${Math.round(v / 1000)}k` : `£${Math.round(v)}`;
    if (metric === 'cpl' || metric === 'cpa' || metric === 'cpc' || metric === 'cpm') return `£${Math.round(v)}`;
    if (metric === 'roas') return `${v.toFixed(1)}×`;
    if (metric === 'ctr' || metric === 'cvr') return `${(v * 100).toFixed(1)}%`;
    return num(v);
  };

  return (
    <div className="ins-shape">
      <div className="ins-shape-head">
        <span className="ins-shape-title">{labelOf(labels, metric)} · daily</span>
        {hasGhost && (
          <span className="ins-shape-note">
            <svg width="26" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="26" y2="4" stroke={colours.ghost} strokeWidth="1.6" strokeDasharray="4 4" />
            </svg>
            previous {days} days
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={series} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
          <CartesianGrid stroke={colours.grid} strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            tick={{ fontSize: 11, fill: colours.axis }}
            axisLine={{ stroke: colours.grid }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={42}
          />
          <YAxis
            tickFormatter={axisFormat}
            tick={{ fontSize: 11, fill: colours.axis }}
            axisLine={false}
            tickLine={false}
            width={54}
          />
          <Tooltip
            labelFormatter={shortDate}
            formatter={(v, name) => [
              fmt(metric, v),
              name === 'prev' ? `Previous ${days} days` : labelOf(labels, metric),
            ]}
            contentStyle={{
              background: 'var(--surface)',
              border: `1px solid ${colours.grid}`,
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'var(--font-body)',
            }}
          />
          <Area type="monotone" dataKey={metric} stroke="none" fill={colours.line} fillOpacity={0.11} />
          {hasGhost && (
            <Line type="monotone" dataKey="prev" stroke={colours.ghost} strokeWidth={1.6}
                  strokeDasharray="4 4" dot={false} />
          )}
          <Line type="monotone" dataKey={metric} stroke={colours.line} strokeWidth={2.2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function RankTable({ rows, dim, metrics, sortMetric, labels }) {
  const columns = (metrics || []).slice(0, 5);
  const barMetric = sortMetric && columns.includes(sortMetric) ? sortMetric : columns[0];
  const max = Math.max(...rows.map((r) => r[barMetric] || 0), 0);

  return (
    <div className="ins-shape">
      <div className="ins-shape-head">
        <span className="ins-shape-title">By {labelOf(labels, dim).toLowerCase()}</span>
        <span className="ins-shape-note">
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
          {barMetric
            ? ` · bars show ${labelOf(labels, barMetric).toLowerCase()}${
                LOWER_IS_BETTER.has(barMetric) ? ', where longer is worse' : ''}`
            : ''}
        </span>
      </div>
      <div className="ins-table-wrap">
        <table className="ins-table">
          <thead>
            <tr>
              <th>{labelOf(labels, dim)}</th>
              {columns.map((k) => (
                <th key={k} className="num">{labelOf(labels, k)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r[dim] ?? i}>
                <td className="ins-dim">{r[dim] ?? '—'}</td>
                {columns.map((k) => {
                  const isBar = k === barMetric && max > 0;
                  const width = isBar ? Math.max(3, ((r[k] || 0) / max) * 100) : 0;
                  return (
                    <td key={k} className={`num${isBar ? ' mag' : ''}`}>
                      {isBar && <span className="bar" style={{ width: `calc(${width}% - 13px)` }} />}
                      <span className="v">{r[k] == null ? '—' : fmt(k, r[k])}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Provenance, as a sentence rather than a JSON dump
   ───────────────────────────────────────────────────────────────────────── */

function howItWasCalculated(msg, scopeLabel) {
  const { plan, spec, result } = msg;
  const bits = [];

  // "N daily rows" was true when the browser scanned generated rows and
  // counted them. The warehouse returns the ANSWER, so that number became the
  // number of result rows — and a single total rendered as "1 daily rows",
  // which reads like the account has one day of data in it.
  //
  // What is honest now is what was read and how: the client, the grain, and
  // the shape of the result.
  bits.push(plan.grain ? `${scopeLabel} · ${plan.grain} grain` : scopeLabel);
  bits.push(rangeText(result.range));

  if (spec.campaignFilter?.length) bits.push(`campaigns matching "${spec.campaignFilter.join('", "')}"`);
  if (spec.dimension) {
    bits.push(
      `grouped by ${(DIM_LABELS[spec.dimension] || spec.dimension).toLowerCase()}`
      + ` (${num(result.rowCount)} ${result.rowCount === 1 ? 'row' : 'rows'})`
    );
  } else {
    bits.push('summed to a single total');
  }
  if (spec.sort?.metric) {
    bits.push(`ranked by ${labelOf(msg.labels, spec.sort.metric).toLowerCase()}${spec.sort.dir === 'asc' ? ', lowest first' : ', highest first'}`);
  }
  if (spec.limit) bits.push(`top ${spec.limit}`);
  if (plan.comparePrevious) bits.push('compared against the preceding window of equal length');

  return bits.join(' · ');
}

/* ─────────────────────────────────────────────────────────────────────────
   Answer card
   ───────────────────────────────────────────────────────────────────────── */

function Answer({ msg, scopeLabel, pinned, onPin }) {
  const [copied, setCopied] = useState(false);
  const {
    plan, spec, result, comparison, answer, totals, previousTotals,
    caveats = [],
  } = msg;

  const dim = spec.dimension;
  const rows = result.results;
  const chartMetric = (plan.metrics || []).find((m) => rows.some((r) => r[m] != null)) || 'spend';

  const copyFigures = useCallback(() => {
    const keys = dim ? [dim, ...(plan.metrics || []).slice(0, 5)] : (plan.metrics || []).slice(0, 5);
    const header = keys.map((k) => labelOf(msg.labels, k)).join('\t');
    const body = rows.map((r) => keys.map((k) => (k === dim ? r[k] : (r[k] == null ? '' : r[k]))).join('\t')).join('\n');
    const text = `${msg.question}\n${rangeText(result.range)}\n\n${header}\n${body}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }).catch(() => {});
    }
  }, [dim, plan.metrics, rows, msg.question, result.range]);

  return (
    <div className="ins-card">
      <Figures
        labels={msg.labels}
        metrics={plan.metrics}
        totals={totals}
        previous={previousTotals}
        range={result.range}
        limited={spec.limit && rows.length >= spec.limit ? spec.limit : null}
      />

      {dim === 'date' && rows.length > 1 && (
        <TimeChart
          labels={msg.labels}
          rows={rows}
          previousRows={comparison?.results}
          metric={chartMetric}
          days={plan.rangeDays}
        />
      )}

      {dim && RANKING_DIMS.has(dim) && rows.length > 0 && (
        <RankTable rows={rows} dim={dim} metrics={plan.metrics} sortMetric={spec.sort?.metric} labels={msg.labels} />
      )}

      <div className="ins-prose">
        {String(answer || '').split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
      </div>

      {/*
        WHAT THE FIGURES DO AND DO NOT SUPPORT.
        Conversions still arriving for recent days; a conversion mapping the
        client has not confirmed; a grain that covers one platform only. These
        come from the warehouse with the answer and are shown rather than
        dropped. A cost per conversion built on a mapping nobody at the client
        has agreed to is indicative, and the difference between saying so and
        not saying so is the difference between a governance product and a
        dashboard.
      */}
      {caveats.length > 0 && (
        <ul className="ins-caveats">
          {caveats.map((caveat, i) => <li key={i}>{caveat}</li>)}
        </ul>
      )}

      <div className="ins-foot">
        <div className="ins-how">
          <b>How this was calculated.</b>{' '}
          {howItWasCalculated(msg, scopeLabel)}. Every figure is computed from the account
          data; the wording is written by the model from those figures — it does no arithmetic
          of its own.
        </div>
        <div className="ins-acts">
          <button className="ins-act" onClick={copyFigures}>
            <Icon name={copied ? 'check' : 'layers'} size={13} strokeWidth={2.4} />
            {copied ? 'Copied' : 'Copy figures'}
          </button>
          <button
            className={`ins-act${pinned ? ' on' : ''}`}
            onClick={onPin}
            aria-pressed={pinned}
          >
            <Icon name="pin" size={13} strokeWidth={2.4} />
            {pinned ? 'Pinned' : 'Pin to report'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */

export default function AIInsights() {
  const { selectedId, selected, clients, isAll } = useClient();

  // Two different things, deliberately kept apart: the id sent to the server
  // for scope checking, and the set of clients whose figures to compute over.
  const clientId = isAll ? null : selectedId;
  const dataKeys = useMemo(
    () => (isAll ? clients.map((c) => c.client_id) : [selectedId]),
    [isAll, clients, selectedId]
  );
  const scopeKey = dataKeys.join('|');
  const scopeLabel = isAll ? `all clients (${clients.length})` : (selected?.name || 'this client');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [range, setRange] = useState(null);
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pins, setPins] = useState(() => loadPins(dataKeys.join('|')));
  const [rangeOpen, setRangeOpen] = useState(false);

  const promptRef = useRef(null);
  const endRef = useRef(null);
  const rangeRef = useRef(null);

  // Switching client invalidates the conversation — those figures belonged to
  // another account, and leaving them on screen would be misleading.
  useEffect(() => {
    setMessages([]);
    setPins(loadPins(scopeKey));
  }, [scopeKey]);

  useEffect(() => {
    if (messages.length || busy) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, busy]);

  useEffect(() => {
    if (!rangeOpen) return undefined;
    const close = (e) => { if (!rangeRef.current?.contains(e.target)) setRangeOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [rangeOpen]);

  // The textarea grows with its content rather than scrolling inside itself.
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  const rangeLabel = RANGE_OPTIONS.find((o) => o.value === range)?.label || 'Let the question decide';

  async function submit(question) {
    const typed = (question ?? input).trim();
    if (!typed || busy) return;

    // The chip composes the question rather than passing a parameter, so the
    // /reporting/ask contract is untouched and what is sent is what is shown.
    // (It named /reporting/plan, a route that no longer exists - planning
    // moved into the reporting service with the figures it plans against.)
    const composed = range ? `${typed} — over the last ${range} days` : typed;

    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: composed, added: range ? ` — over the last ${range} days` : '' }]);

    try {
      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'agent')
        .slice(-4)
        .map((m) => ({ role: m.role, text: m.role === 'user' ? m.text : m.answer }));

      // The totals arrive with the answer now, recomputed by the warehouse
      // from the period's summed numerator and denominator rather than
      // averaged across the grouped rows. They used to be a second query run
      // here in the browser; the figure the narrator was given and the figure
      // on screen were computed in different places and could disagree.
      const {
        plan, spec, result, comparison, answer, totals, previousTotals, caveats,
        labels,
      } = await askAgent(composed, clientId, history);

      setMessages((m) => [...m, {
        role: 'agent',
        id: `${Date.now()}`,
        question: composed,
        plan, spec, result, comparison, answer, totals, previousTotals, caveats,
        labels,
      }]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'error', text: err.message || 'That question could not be answered.' }]);
    } finally {
      setBusy(false);
    }
  }

  const togglePin = useCallback((msg) => {
    setPins((current) => {
      const exists = current.some((p) => p.id === msg.id);
      const next = exists
        ? current.filter((p) => p.id !== msg.id)
        : [{
            id: msg.id,
            question: msg.question,
            metric: (msg.plan.metrics || [])[0] || null,
            metricLabel: labelOf(msg.labels, (msg.plan.metrics || [])[0]),
            value: msg.totals ? msg.totals[(msg.plan.metrics || [])[0]] : null,
            range: rangeText(msg.result.range),
            when: new Date().toISOString(),
          }, ...current].slice(0, MAX_PINS);
      savePins(scopeKey, next);
      return next;
    });
  }, [scopeKey]);

  const removePin = useCallback((id) => {
    setPins((current) => {
      const next = current.filter((p) => p.id !== id);
      savePins(scopeKey, next);
      return next;
    });
  }, [scopeKey]);

  const clearPins = useCallback(() => {
    setPins([]);
    savePins(scopeKey, []);
  }, [scopeKey]);

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">AI Insights</h1>
          <p className="page-subtitle">
            Ask a question about {scopeLabel} in plain English. Every figure is calculated from
            the account data, never estimated by the model.
          </p>
        </div>
        {messages.length > 0 && (
          <div className="hdr-actions">
            <button className="btn btn-secondary" onClick={() => setMessages([])} disabled={busy}>
              <Icon name="plus" size={14} strokeWidth={2.4} />
              New question
            </button>
          </div>
        )}
      </div>

      {pins.length > 0 && (
        <div className="ins-pinned">
          <div className="ins-pinned-head">
            <Icon name="pin" size={13} strokeWidth={2.4} />
            Pinned for the report · {pins.length}
            <button className="ins-act" onClick={clearPins}>Clear all</button>
          </div>
          {pins.map((p) => (
            <div className="ins-pin" key={p.id}>
              <span className="ins-pin-q">{p.question}</span>
              {p.metric && p.value != null && (
                <span className="ins-pin-fig">
                  {p.metricLabel || METRIC_LABELS[p.metric] || p.metric} {fmt(p.metric, p.value)}
                </span>
              )}
              <span className="ins-pin-when">{p.range}</span>
              <button className="ins-pin-x" onClick={() => removePin(p.id)} aria-label="Remove pin">
                <Icon name="x" size={14} strokeWidth={2.4} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── The composer ── */}
      <div className={`composer ${focused ? 'focused' : ''} ${busy ? 'busy' : ''}`}>
        <div className="composer-top">
          <Icon name="spark" size={20} strokeWidth={1.8} className="composer-spark" />
          <textarea
            ref={promptRef}
            rows={1}
            className="composer-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            placeholder="Ask about performance — leads, cost per lead, spend, which campaign…"
            disabled={busy}
            aria-label="Ask a question about performance"
          />
        </div>

        <div className="composer-controls">
          <span className="chip" style={{ cursor: 'default' }}>
            <Icon name="user" size={13} />
            <span>{isAll ? `All clients (${clients.length})` : (selected?.name || 'This client')}</span>
          </span>

          <div className={`chip ${rangeOpen ? 'open' : ''}`} ref={rangeRef}>
            <Icon name="clock" size={13} />
            <button
              type="button"
              onClick={() => !busy && setRangeOpen((o) => !o)}
              disabled={busy}
              aria-haspopup="listbox"
              aria-expanded={rangeOpen}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: busy ? 'default' : 'pointer',
                font: 'inherit', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>{rangeLabel}</span>
              <Icon name="down" size={12} strokeWidth={2.6} />
            </button>

            {rangeOpen && (
              <div className="chip-menu" role="listbox">
                {RANGE_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={String(opt.value)}
                    role="option"
                    aria-selected={range === opt.value}
                    className={`chip-opt ${range === opt.value ? 'sel' : ''}`}
                    onClick={() => { setRangeOpen(false); setRange(opt.value); promptRef.current?.focus(); }}
                  >
                    <span className="o-main">{opt.label}</span>
                    <span className="o-tick"><Icon name="check" size={14} strokeWidth={2.6} /></span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className="ctrl-spacer" />

          <button
            className="btn-build"
            onClick={() => submit()}
            disabled={busy || !input.trim()}
          >
            {busy ? 'Working…' : 'Ask'}
            <Icon name="right" size={14} strokeWidth={2.6} />
          </button>
        </div>
      </div>

      {/* ── Starting points ── */}
      {messages.length === 0 && !busy && (
        <>
          <div className="suggestions">
            {QUICK.map((s) => (
              <button key={s.text} className="sug" onClick={() => submit(s.text)}>
                <i style={{ background: s.colour }} />
                {s.text}
              </button>
            ))}
          </div>

          <div className="section-lede">Or start from a theme</div>
          <div className="topics">
            {TOPICS.map((t) => (
              <button
                key={t.title}
                className="topic"
                onClick={() => { setInput(t.q); promptRef.current?.focus(); }}
              >
                <div className="topic-h">
                  {t.title}
                  <Icon name="chevron" size={14} strokeWidth={2.4} />
                </div>
                <p>{t.blurb}</p>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── The thread ── */}
      <div className="ins-thread">
        {messages.map((m, i) => {
          if (m.role === 'user') {
            return (
              <div key={i} className="ins-q">
                <Icon name="search" size={16} strokeWidth={2.2} />
                <span>
                  {m.added ? m.text.slice(0, m.text.length - m.added.length) : m.text}
                  {m.added && <span className="ins-added">{m.added}</span>}
                </span>
              </div>
            );
          }
          if (m.role === 'error') {
            return (
              <div key={i} className="ins-err">
                <Icon name="alert" size={16} strokeWidth={2.2} />
                <span>{m.text}</span>
              </div>
            );
          }
          return (
            <Answer
              key={m.id || i}
              msg={m}
              scopeLabel={scopeLabel}
              pinned={pins.some((p) => p.id === m.id)}
              onPin={() => togglePin(m)}
            />
          );
        })}

        {busy && (
          <div className="ins-busy">
            <span className="ins-dot" />
            Working out the answer
            <span className="ins-busy-step">· the figures first, then the wording</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/*
        This page is no longer illustrative, so it no longer says it is.

        The disclaimer below used to read "these figures are generated to
        demonstrate the interface and do not come from a live ad account".
        That was true and important while the numbers came from metrics.js in
        the browser. It is now false: every figure is computed by BigQuery
        from this client's warehouse.

        The Dashboard and Opti-Bid pages keep theirs, because theirs is still
        true. A disclaimer left in place after it stops being accurate is
        worse than none — it teaches people to ignore the one that matters.

        What the data IS, is said per answer instead: the caveats under each
        card carry the conversion mapping's confirmed state and the maturity
        of recent conversions, which is the honest version of this.
      */}
      <p className="rp-disclaimer">
        <strong>Account data.</strong> Every figure is computed from this client&apos;s
        warehouse by the reporting service — the model plans the query and writes the
        wording, and does no arithmetic. Check the notes under an answer for what each
        figure does and does not support.
      </p>
    </>
  );
}
